import Loading from '@/components/Loading/Loading';
import { _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import type { AgentSessionApiId, AgentTreeNodeApiId, ScorecardSeverity } from '@/model/Agent.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import type { FC } from 'react';
import styles from './AgentReport.module.scss';
import ScorecardCoverage from './ScorecardCoverage';
import ScorecardFinding from './ScorecardFinding';
import ScorecardSnapshot from './ScorecardSnapshot';

interface AgentReportProps {
  /** The session the report node belongs to. */
  sessionApiId: AgentSessionApiId;
  /** The report-variant tree node whose report to fetch and render. */
  nodeApiId: AgentTreeNodeApiId;
  /**
   * Display name of the agent that produced the report, from `agentDisplayMap`. Supplied by the
   * caller rather than inferred here: any agent can emit a report node, so this modal holds no agent
   * knowledge of its own — the same reason the tree maps node variants without it.
   */
  agentName?: string;
  /** Injected by the modal system; closes the modal. */
  closeModal: () => void;
}

/**
 * Most urgent first, so a reader meets the findings needing action before the reassuring ones. An
 * unrecognised severity sorts to the top rather than yielding NaN, which would make the comparator
 * inconsistent and scatter the whole list.
 */
const severityOrder: Record<ScorecardSeverity, number> = { critical: 0, warning: 1, info: 2 };
const rankOf = (severity: ScorecardSeverity): number => severityOrder[severity] ?? -1;

/**
 * Modal that fetches and renders an agent result report. Opened from a result node's "View Report"
 * action, which supplies the node identity; the modal owns the fetch so it can open instantly with a
 * loading state (the fetch can be slow) and so a repeated click just re-opens the modal rather than
 * firing duplicate requests.
 */
const AgentReport: FC<AgentReportProps> = ({ sessionApiId, nodeApiId, agentName, closeModal }) => {
  const { response, loading } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}/nodes/{nodeApiId}/content',
    path: { sessionApiId, nodeApiId },
    options: {
      onError: {
        throw: false,
        preserveOriginalError: false,
        toast: false,
        capture: true,
        slug: 'agent-node-report-load',
      },
    },
  });

  const report = response?.type === 'success' ? response.body : null;
  // Which agent, and which study — both identify the report, so they belong in the title rather
  // than repeated as a heading in the body. Each part is dropped if unavailable, so the title
  // degrades to "Report" rather than showing a dangling separator.
  const title = [agentName, report?.snapshot?.name].filter(_isNotBlank).join(' - ');
  // Ordered here rather than trusted from the response, so a severity the UI does not recognise
  // cannot scatter the list.
  const orderedFindings = [...(report?.findings ?? [])].sort((a, b) => rankOf(a.severity) - rankOf(b.severity));
  // Coverage counts alone are content: a study with nothing recorded yet has no findings and may
  // have no snapshot, but its record totals are still the answer to "what is in this study".
  const hasContent =
    _notNil(report) && (_isNotEmpty(orderedFindings) || _notNil(report.snapshot) || _notNil(report.coverage));

  if (loading) {
    return <Loading txt="Loading report" />;
  }

  return (
    <ModalContainer size="medium" testKey="agent-report-modal">
      <ModalHeader
        title={_isNotBlank(title) ? title : 'Report'}
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
      />
      {response?.type === 'error' ? (
        <div className={styles.body} data-testid="agent-report-content">
          <p className="gray tc" data-test-element="error">
            Couldn't load the report.
          </p>
        </div>
      ) : hasContent ? (
        <div className={styles.body} data-testid="agent-report-content">
          {_notNil(report.snapshot) ? <ScorecardSnapshot snapshot={report.snapshot} /> : null}

          {_isNotEmpty(orderedFindings) ? (
            <ul className={styles.findings} data-test-element="findings">
              {orderedFindings.map((finding, index) => (
                // Findings carry no server id. Keying on the headline rather than the index keeps a
                // finding's expanded state attached to it if the list is ever re-sorted; the index
                // is still used for the DOM id, which only needs to be unique within this render.
                <ScorecardFinding
                  key={`${finding.severity}-${finding.finding}`}
                  finding={finding}
                  id={`agent-report__finding--${index}`}
                />
              ))}
            </ul>
          ) : null}

          {_notNil(report.coverage) ? <ScorecardCoverage coverage={report.coverage} /> : null}
        </div>
      ) : (
        <div className={styles.body} data-testid="agent-report-content">
          <p className="gray tc" data-test-element="empty-state">
            No report content.
          </p>
        </div>
      )}
      <div className="pa3 bt b--moon-gray">
        <ModalActions onCancel={closeModal} cancelBtnText="Close" />
      </div>
    </ModalContainer>
  );
};

export default AgentReport;
